import React from 'react';
import { motion } from 'framer-motion';
import { Droplets, CheckCircle, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const AgricultureIndustryPage: React.FC = () => {
  const systems = [
    {
      title: 'Inline Pressure Filters',
      description: 'Automatic or manual cleaning options sized for every flow range',
      link: '/filtration/inline/big'
    },
    {
      title: 'Self-Cleaning Suction Screens',
      description: 'Protect pumps at the source with automated spray-nozzle cleaning',
      link: '/filtration/suction-screens/self-cleaning'
    },
    {
      title: 'Water Box & Specialty',
      description: 'High-flow intake protection for canal and pump-station integration',
      link: '/filtration/water-intake'
    }
  ];

  const benefits = [
    'Extend emitter life by removing particles before they reach drip lines',
    'Stabilize system pressure with consistent, reliable filtration',
    'Simplify maintenance with automated or semi-automated cleaning',
    'Compatible with fertigation and pH control systems',
    'Stainless-steel construction for long service life',
    'Custom mesh sizes from 20 to 150 mesh for precise particle removal'
  ];

  const applications = [
    {
      title: 'Orchard and Vineyard Irrigation',
      description: 'Protect micro-sprinkler and drip systems from scale, algae, and organic debris in canal or well water.'
    },
    {
      title: 'Drip and Micro Systems',
      description: 'Maintain consistent flow rates and prevent emitter clogging with fine-mesh filtration.'
    },
    {
      title: 'Pivot and Solid-Set',
      description: 'Coarser filtration for overhead irrigation using perforated plate or low-mesh screens.'
    },
    {
      title: 'Dairy Lagoons & Manure Ponds',
      description: 'SCS2 self-cleaning screens proven reliable in high-solids, fibrous slurry environments for lagoon pumping and effluent irrigation systems.'
    },
    {
      title: 'Reclaimed Water Reuse',
      description: 'Handle variable debris loads in recycled water with self-cleaning pressure filters.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Agricultural Filtration Systems | CTC Waterworks"
        description="Protect irrigation networks, orchards, and vineyards with CTC filtration systems. Extend emitter life, stabilize pressure, and simplify maintenance across drip, micro, and pivot systems."
        canonical="https://www.ctcwaterworks.com/industries/agriculture"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                    <Droplets className="text-white" size={32} />
                  </div>
                  <h1 className="text-5xl font-bold">
                    Agriculture
                  </h1>
                </div>
                <p className="text-2xl text-green-100 mb-6 font-light leading-relaxed">
                  CTC began in agriculture — protecting irrigation networks, orchards, and vineyards from debris and scale.
                </p>
                <p className="text-lg text-green-100 leading-relaxed">
                  Our filtration systems extend emitter life, stabilize pressure, and simplify maintenance across drip, micro, and pivot systems.
                </p>
              </motion.div>

              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>
                <Link
                  to="/contact?topic=agriculture-filtration"
                  className="block w-full bg-green-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors text-center shadow-lg mb-4"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  Our team will help you size the right filter for your irrigation system
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Why CTC Filtration for Agriculture?
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Built specifically for the demands of agricultural water systems
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-green-50 border border-green-200 rounded-lg p-6">
                  <CheckCircle className="mr-3 text-green-600 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700 leading-relaxed">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Common Applications */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Common Applications
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              CTC filtration systems protect every type of agricultural irrigation
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {applications.map((app, index) => (
                <div key={index} className="bg-white border-2 border-green-200 rounded-lg p-8 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Overview */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Recommended Systems
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Choose from inline filters, suction screens, or water box assemblies
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {systems.map((system, index) => (
                <div key={index} className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg p-6 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{system.title}</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">{system.description}</p>
                  <Link
                    to={system.link}
                    className="flex items-center justify-between bg-white border-2 border-green-600 text-green-600 px-4 py-3 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-colors"
                  >
                    <span>Learn More</span>
                    <ArrowRight size={20} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Highlight */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white border-2 border-green-300 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Fertigation & Chemical Injection Compatible
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                All CTC inline filters are compatible with fertigation systems, pH control, and chemical injection.
                Stainless-steel construction resists corrosion from fertilizers and acids, ensuring long-term reliability
                in demanding agricultural environments.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">Fertilizer Safe</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">Acid Resistant</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">Chemical Compatible</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-green-600 to-green-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Irrigation System?</h2>
            <p className="text-xl text-green-100 mb-8">
              Our engineering team will help you select the right filtration system for your crops, water source, and irrigation method.
            </p>
            <Link
              to="/contact?topic=agriculture-filtration"
              className="inline-block bg-white text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Talk to a Filtration Expert
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AgricultureIndustryPage;
